import { endpoints } from '@/api'
import type { PublicBranding } from '@/api/types'
import { applyBrand, useBrandStore } from './brand-store'

const CACHE_PREFIX = 'recoup.brand.'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

interface CachedBrand {
  readonly savedAt: number
  readonly brand: PublicBranding
}

function cacheKey(host: string): string {
  return `${CACHE_PREFIX}${host}`
}

/** localStorage is a per-viewer convenience only; every read/write is guarded (it can throw). */
export function readCachedBrand(host: string, now = Date.now()): PublicBranding | null {
  try {
    const raw = localStorage.getItem(cacheKey(host))
    if (raw === null) return null
    const parsed = JSON.parse(raw) as Partial<CachedBrand>
    if (typeof parsed.savedAt !== 'number' || parsed.brand === undefined) return null
    if (now - parsed.savedAt > CACHE_TTL_MS) return null
    return parsed.brand
  } catch {
    return null
  }
}

export function writeCachedBrand(host: string, brand: PublicBranding, now = Date.now()): void {
  try {
    localStorage.setItem(
      cacheKey(host),
      JSON.stringify({ savedAt: now, brand } satisfies CachedBrand),
    )
  } catch {
    // Storage unavailable (private mode, quota) — the resolved brand still applies for this visit.
  }
}

/**
 * Brand before auth (spec §13, FRONTEND_PLAN §3.1): apply the cached palette for this host at
 * once, then resolve from the API and update. Never throws — an unbranded page beats no page.
 */
export async function bootstrapBrand(host: string = globalThis.location.host): Promise<void> {
  const cached = readCachedBrand(host)
  if (cached !== null) {
    useBrandStore.getState().setBrand(cached, 'cache')
    applyBrand(cached)
  }
  try {
    const resolved = await endpoints.branding.resolve()
    useBrandStore.getState().setBrand(resolved, 'resolved')
    applyBrand(resolved)
    writeCachedBrand(host, resolved)
  } catch {
    // Keep whatever is applied (cache or platform default).
  }
}
